import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle
} from '@/components/ui/dialog';
import { BarChart3, Lock, Mail, ArrowRight, CheckCircle2, Building2, KeyRound, ShieldCheck } from 'lucide-react';
import { loadAndApplyBranding, applyBrandingToDOM } from '@/lib/branding';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const SSO_LABELS = { saml: 'SAML', oidc: 'Single Sign-On', ldap: 'Directory (LDAP)' };
const SSO_ERRORS = {
  oidc_failed: 'Single sign-on failed. Please try again.',
  oidc_bad_state: 'Login session expired. Please retry.',
  oidc_no_email: 'Your identity provider did not return an email address.',
  saml_failed: 'SAML authentication failed. Please contact your admin.',
  saml_invalid: 'The SAML response was invalid.',
  saml_no_email: 'Your identity provider did not return an email address.',
};

export default function LoginPage({ branding: brandingProp = null }) {
  const { login, googleLogin, appConfig } = useAuth();
  const navigate = useNavigate();
  // On the plain root sign-in page (no branded slug), when the deployment's
  // root page is set to "Sign-in page" (root_page === 'login'), fall back to
  // the SYSTEM tenant's branding so the base-URL login is branded too.
  const sysBrand = (!brandingProp && appConfig?.root_page === 'login' && appConfig?.system_branding) || null;
  const branding = brandingProp || (sysBrand ? {
    logo: sysBrand.logo_url_dark || sysBrand.logo_url || '',
    app_name: sysBrand.app_name || '',
    tagline: sysBrand.login_tagline || '',
    bgImage: sysBrand.login_bg_image_url || '',
    primary: sysBrand.primary_color || '',
  } : null);
  // Apply system branding to the document (favicon, title, CSS vars) so the
  // browser tab and themed controls match before the user signs in.
  useEffect(() => { if (sysBrand) applyBrandingToDOM(sysBrand); }, [sysBrand]);
  // Branded pages theme buttons/links with the tenant's primary color.
  const brandPrimary = branding?.primary || null;
  // Login backdrop + tagline: a branded slug uses its tenant's values; the
  // plain /login falls back to the deployment default (platform app-config),
  // then to the built-in image.
  const DEFAULT_LOGIN_BG = 'https://images.unsplash.com/photo-1762430047160-db0e2670c533?w=1920&q=80';
  const loginBg = branding?.bgImage || appConfig?.login_bg_image_url || DEFAULT_LOGIN_BG;
  const loginTagline = branding ? branding?.tagline : (appConfig?.login_tagline || '');
  const brandBtnStyle = brandPrimary ? { backgroundColor: brandPrimary, boxShadow: `0 0 20px ${brandPrimary}55` } : undefined;
  const brandLinkStyle = brandPrimary ? { color: brandPrimary } : undefined;
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [forgotOpen, setForgotOpen] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotSent, setForgotSent] = useState(false);
  const [forgotLoading, setForgotLoading] = useState(false);

  // SSO flow
  const [ssoOpen, setSsoOpen] = useState(false);
  const [ssoEmail, setSsoEmail] = useState('');
  const [ssoStep, setSsoStep] = useState('email'); // email | choose | ldap
  const [ssoProviders, setSsoProviders] = useState([]);
  const [ssoTenant, setSsoTenant] = useState('');
  const [ssoPassword, setSsoPassword] = useState('');
  const [ssoLoading, setSsoLoading] = useState(false);
  const [ssoMsg, setSsoMsg] = useState('');

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const code = params.get('sso_error');
    if (code) setError(SSO_ERRORS[code] || 'Single sign-on could not be completed.');
  }, []);

  const handleSsoDiscover = async (e) => {
    e.preventDefault();
    setSsoMsg(''); setSsoLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/sso/discover`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: ssoEmail }),
      });
      const data = await res.json();
      if (!data.sso || !data.providers?.length) { setSsoMsg('No SSO is configured for this email domain.'); return; }
      setSsoProviders(data.providers); setSsoTenant(data.tenant_id);
      if (data.providers.length === 1 && data.providers[0] === 'ldap') setSsoStep('ldap');
      else setSsoStep('choose');
    } catch { setSsoMsg('Could not check SSO. Please try again.'); }
    finally { setSsoLoading(false); }
  };

  const startRedirectSso = (provider) => {
    window.location.href = `${BACKEND_URL}/api/sso/${provider}/${ssoTenant}/login`;
  };

  const handleLdapLogin = async (e) => {
    e.preventDefault();
    setSsoMsg(''); setSsoLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/sso/ldap/login`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify({ email: ssoEmail, password: ssoPassword }),
      });
      if (!res.ok) { const d = (await res.json().catch(() => null)) ?? {}; setSsoMsg((typeof d.detail === 'string' && d.detail) || 'Directory login failed.'); return; }
      const nextUrl = new URLSearchParams(window.location.search).get('next');
      window.location.href = (nextUrl && nextUrl.startsWith('/')) ? nextUrl : '/dashboard'; // full reload so AuthContext picks up the session cookie
    } catch { setSsoMsg('Directory login failed.'); }
    finally { setSsoLoading(false); }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await login(loginEmail, loginPassword, branding?.slug || null);
      // Apply this tenant's branding BEFORE routing into the app so the
      // dashboard renders already branded (no INZARA→tenant flash).
      await loadAndApplyBranding();
      // If the user was sent here from a protected link (e.g. a shared
      // Insight URL), return them to that destination after signing in.
      const nextUrl = new URLSearchParams(window.location.search).get('next');
      if (nextUrl && nextUrl.startsWith('/')) {
        navigate(nextUrl);
        return;
      }
      // First-run experience: brand-new tenants land on the Marketplace's
      // Sample Datasets banner so they can fire up a working demo in 5
      // seconds instead of staring at an empty dashboard. Persisted per
      // user-account so existing users keep their normal home.
      const seenKey = `li-first-login-${loginEmail.toLowerCase()}`;
      if (!localStorage.getItem(seenKey)) {
        localStorage.setItem(seenKey, new Date().toISOString());
        navigate('/marketplace');
      } else {
        navigate('/dashboard');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!forgotEmail.trim()) return;
    setForgotLoading(true);
    try {
      await fetch(`${BACKEND_URL}/api/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: forgotEmail }),
      });
      // Always show success to prevent email enumeration
      setForgotSent(true);
    } catch {
      setForgotSent(true);
    } finally {
      setForgotLoading(false);
    }
  };

  return (
    <div className="min-h-screen relative flex items-center justify-center overflow-hidden" data-testid="login-page">
      {/* Background */}
      <div className="absolute inset-0 bg-gradient-to-br from-zinc-950 via-zinc-900 to-zinc-950" />
      <div className="absolute inset-0 opacity-30"
        style={{ backgroundImage: `url(${loginBg})`, backgroundSize: 'cover', backgroundPosition: 'center' }}
      />
      <div className="absolute inset-0 bg-black/60" />

      {/* Floating orbs */}
      <div className="absolute top-1/4 left-1/4 w-64 h-64 rounded-full bg-blue-500/10 blur-3xl animate-float" />
      <div className="absolute bottom-1/4 right-1/4 w-80 h-80 rounded-full bg-indigo-500/10 blur-3xl animate-float" style={{ animationDelay: '3s' }} />

      {/* Login card */}
      <div className="relative z-10 w-full max-w-md mx-4 animate-slide-up">
        <div className="glass-card rounded-2xl p-8 shadow-2xl" style={{ background: 'rgba(9, 9, 11, 0.75)', border: '1px solid rgba(255,255,255,0.1)' }}>
          <h1 className="sr-only">Sign in to {branding?.app_name || 'INZARA'}</h1>
          {/* Logo */}
          <div className="flex flex-col items-center mb-8">
            {branding?.logo ? (
              <img src={branding.logo} alt={branding.app_name || 'Logo'} className="h-20 object-contain" data-testid="branded-login-logo" />
            ) : branding?.app_name ? (
              <div className="h-20 flex items-center justify-center" data-testid="branded-login-wordmark">
                <span className="text-3xl font-heading font-bold text-white text-center">{branding.app_name}</span>
              </div>
            ) : (
              <img src="/inzara-dark.png" alt="INZARA" className="h-20 object-contain" />
            )}
            {branding?.app_name ? (
              <p className="mt-3 text-sm text-zinc-400" data-testid="branded-login-name">Sign in to {branding.app_name}</p>
            ) : null}
            {loginTagline ? (
              <p className="mt-1 text-xs text-zinc-500 text-center max-w-xs" data-testid="branded-login-tagline">{loginTagline}</p>
            ) : null}
          </div>

          {error && (
            <div className="mb-4 p-3 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 text-sm" data-testid="login-error">
              {error}
            </div>
          )}

          <form onSubmit={handleLogin} className="space-y-4">
            <div className="space-y-2">
              <Label className="text-zinc-300 text-sm">Email</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                <Input
                  data-testid="login-email"
                  type="text"
                  placeholder="analyst@company.com"
                  value={loginEmail}
                  onChange={e => setLoginEmail(e.target.value)}
                  className="pl-10 bg-white/5 border-white/10 text-white placeholder:text-zinc-600 focus:border-blue-500/50 rounded-xl"
                  required
                />
              </div>
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-zinc-300 text-sm">Password</Label>
                <button
                  type="button"
                  onClick={() => { setForgotEmail(loginEmail); setForgotSent(false); setForgotOpen(true); }}
                  className="text-xs text-blue-400 hover:text-blue-300 transition-colors"
                  style={brandLinkStyle}
                  data-testid="forgot-password-link"
                >
                  Forgot password?
                </button>
              </div>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                <Input
                  data-testid="login-password"
                  type="password"
                  placeholder="Enter password"
                  value={loginPassword}
                  onChange={e => setLoginPassword(e.target.value)}
                  className="pl-10 bg-white/5 border-white/10 text-white placeholder:text-zinc-600 focus:border-blue-500/50 rounded-xl"
                  required
                />
              </div>
            </div>
            <Button
              data-testid="login-submit"
              type="submit"
              disabled={loading}
              style={brandBtnStyle}
              className="w-full rounded-xl bg-blue-500 hover:bg-blue-600 text-white shadow-[0_0_20px_rgba(59,130,246,0.3)] hover:shadow-[0_0_30px_rgba(59,130,246,0.5)] transition-all duration-300 active:scale-95"
            >
              {loading ? 'Signing in...' : 'Sign In'}
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>

            <div className="relative py-1">
              <div className="absolute inset-0 flex items-center"><span className="w-full border-t border-white/10" /></div>
              <div className="relative flex justify-center text-[11px]"><span className="px-2 text-zinc-500 bg-transparent" style={{ background: 'rgba(9,9,11,0)' }}>or</span></div>
            </div>
            <Button
              type="button"
              data-testid="sso-open-btn"
              onClick={() => { setSsoOpen(true); setSsoStep('email'); setSsoEmail(loginEmail); setSsoMsg(''); }}
              variant="outline"
              style={brandPrimary ? { borderColor: `${brandPrimary}66`, color: brandPrimary } : undefined}
              className="w-full rounded-xl bg-white/5 border-white/10 text-zinc-200 hover:bg-white/10 transition-all active:scale-95"
            >
              <Building2 className="w-4 h-4 mr-2" />
              Sign in with SSO
            </Button>

            {!branding && (
              <p className="text-center text-xs text-zinc-500 pt-2">
                Don&apos;t have an account?{' '}
                <button
                  type="button"
                  onClick={() => navigate('/register')}
                  className="text-blue-400 hover:text-blue-300 transition-colors"
                  data-testid="signup-link"
                >
                  Sign up with invite code
                </button>
              </p>
            )}
          </form>
        </div>
      </div>

      {/* Forgot Password Dialog */}
      <Dialog open={forgotOpen} onOpenChange={setForgotOpen}>
        <DialogContent className="glass-card border-border/50 rounded-2xl sm:max-w-sm" style={{ background: 'rgba(9, 9, 11, 0.9)', border: '1px solid rgba(255,255,255,0.1)' }}>
          <DialogHeader>
            <DialogTitle className="font-heading text-white">Reset Password</DialogTitle>
          </DialogHeader>
          {forgotSent ? (
            <div className="text-center py-4">
              <CheckCircle2 className="w-10 h-10 text-emerald-400 mx-auto mb-3" />
              <p className="text-sm text-zinc-300 mb-1">Check your email</p>
              <p className="text-xs text-zinc-500">If an account exists for <span className="text-zinc-300">{forgotEmail}</span>, you will receive a password reset link.</p>
              <Button onClick={() => setForgotOpen(false)} variant="outline" className="mt-4 rounded-xl bg-white/5 border-white/10 text-zinc-300">Back to Sign In</Button>
            </div>
          ) : (
            <form onSubmit={handleForgotPassword} className="space-y-4 mt-2">
              <p className="text-xs text-zinc-400">Enter your email address and we'll send you a link to reset your password.</p>
              <div className="space-y-2">
                <Label className="text-zinc-300 text-sm">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                  <Input
                    data-testid="forgot-email"
                    type="email"
                    placeholder="analyst@company.com"
                    value={forgotEmail}
                    onChange={e => setForgotEmail(e.target.value)}
                    className="pl-10 bg-white/5 border-white/10 text-white placeholder:text-zinc-600 focus:border-blue-500/50 rounded-xl"
                    required
                  />
                </div>
              </div>
              <Button
                data-testid="forgot-submit"
                type="submit"
                disabled={forgotLoading || !forgotEmail.trim()}
                style={brandBtnStyle}
                className="w-full rounded-xl bg-blue-500 hover:bg-blue-600 text-white transition-all active:scale-95"
              >
                {forgotLoading ? 'Sending...' : 'Send Reset Link'}
              </Button>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* SSO Dialog */}
      <Dialog open={ssoOpen} onOpenChange={setSsoOpen}>
        <DialogContent className="glass-card border-border/50 rounded-2xl sm:max-w-sm" style={{ background: 'rgba(9, 9, 11, 0.9)', border: '1px solid rgba(255,255,255,0.1)' }} data-testid="sso-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-white flex items-center gap-2"><ShieldCheck className="w-5 h-5 text-blue-400" /> Single Sign-On</DialogTitle>
          </DialogHeader>

          {ssoMsg && <div className="text-xs text-amber-400 bg-amber-500/10 border border-amber-500/20 rounded-lg p-2" data-testid="sso-message">{ssoMsg}</div>}

          {ssoStep === 'email' && (
            <form onSubmit={handleSsoDiscover} className="space-y-4 mt-2">
              <p className="text-xs text-zinc-400">Enter your work email to continue with your organization's identity provider.</p>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                <Input data-testid="sso-email" type="email" placeholder="you@company.com" value={ssoEmail} onChange={e => setSsoEmail(e.target.value)} required
                  className="pl-10 bg-white/5 border-white/10 text-white placeholder:text-zinc-600 focus:border-blue-500/50 rounded-xl" />
              </div>
              <Button data-testid="sso-continue-btn" type="submit" disabled={ssoLoading || !ssoEmail.trim()} className="w-full rounded-xl bg-blue-500 hover:bg-blue-600 text-white active:scale-95">
                {ssoLoading ? 'Checking...' : 'Continue'}<ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </form>
          )}

          {ssoStep === 'choose' && (
            <div className="space-y-3 mt-2">
              <p className="text-xs text-zinc-400">Choose how to sign in:</p>
              {ssoProviders.filter(p => p !== 'ldap').map(p => (
                <Button key={p} data-testid={`sso-provider-${p}`} onClick={() => startRedirectSso(p)} className="w-full rounded-xl bg-blue-500 hover:bg-blue-600 text-white active:scale-95">
                  Continue with {SSO_LABELS[p] || p}<ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              ))}
              {ssoProviders.includes('ldap') && (
                <Button data-testid="sso-provider-ldap" variant="outline" onClick={() => setSsoStep('ldap')} className="w-full rounded-xl bg-white/5 border-white/10 text-zinc-200 hover:bg-white/10">
                  <KeyRound className="w-4 h-4 mr-2" /> Sign in with directory password
                </Button>
              )}
            </div>
          )}

          {ssoStep === 'ldap' && (
            <form onSubmit={handleLdapLogin} className="space-y-4 mt-2">
              <p className="text-xs text-zinc-400">Sign in with your directory (LDAP/Active Directory) credentials.</p>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                <Input type="email" value={ssoEmail} onChange={e => setSsoEmail(e.target.value)} required
                  className="pl-10 bg-white/5 border-white/10 text-white rounded-xl" />
              </div>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                <Input data-testid="sso-ldap-password" type="password" placeholder="Directory password" value={ssoPassword} onChange={e => setSsoPassword(e.target.value)} required
                  className="pl-10 bg-white/5 border-white/10 text-white placeholder:text-zinc-600 rounded-xl" />
              </div>
              <Button data-testid="sso-ldap-submit" type="submit" disabled={ssoLoading} className="w-full rounded-xl bg-blue-500 hover:bg-blue-600 text-white active:scale-95">
                {ssoLoading ? 'Signing in...' : 'Sign In'}<ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
